import { cn } from '@/lib/utils';
import { useState, type ReactNode } from 'react';
import {
    Users, Camera, UserCheck, Share2, ShoppingBag, MessageCircle,
    GraduationCap, Activity, AlertTriangle, Info, Sparkles, TrendingUp, TrendingDown,
} from 'lucide-react';
import { MetricCard } from '@/components/shared/MetricCard';
import {
    useProductPulse,
    KPI_CATEGORY_LABELS,
    cohortStepPct,
    type ProductKpi,
} from '@/hooks/useProductPulse';
import { ProductPulseTrendCharts } from '@/components/analytics/ProductPulseTrendCharts';
import { ProductQuarterGoalsPanel } from '@/components/analytics/ProductQuarterGoalsPanel';
import { ProductLeanFunnelPanel } from '@/components/analytics/ProductLeanFunnelPanel';
import { ProductGrowthMetricsPanel } from '@/components/analytics/ProductGrowthMetricsPanel';
import { ProductViralCyclePanel } from '@/components/analytics/ProductViralCyclePanel';
import { ProductSessionAnalyticsPanel } from '@/components/analytics/ProductSessionAnalyticsPanel';
import { ProductEngagementPanel } from '@/components/analytics/ProductEngagementPanel';
import { ProductArticleBehaviorPanel } from '@/components/analytics/ProductArticleBehaviorPanel';
import { ANALYTICS_DAY_OPTIONS } from '@/components/analytics/periodOptions';
import { RefreshCw } from 'lucide-react';

const COLORS = {
    ocean: 'hsl(199, 89%, 48%)',
    teal: 'hsl(172, 66%, 50%)',
    amber: 'hsl(38, 92%, 50%)',
    emerald: 'hsl(142, 71%, 45%)',
    purple: 'hsl(262, 83%, 58%)',
    rose: 'hsl(346, 77%, 55%)',
};

const KPI_ICONS: Record<string, ReactNode> = {
    registrations: <Users size={18} />,
    active_users: <Activity size={18} />,
    captures: <Camera size={18} />,
    onboarding_v2_completed: <Sparkles size={18} />,
    profile_completed: <UserCheck size={18} />,
    first_captures: <Camera size={18} />,
    store_orders: <ShoppingBag size={18} />,
    intel_chat_messages: <MessageCircle size={18} />,
    quiz_sessions: <GraduationCap size={18} />,
    share_captures: <Share2 size={18} />,
};

interface ProductPulsePanelProps {
    days: number;
    onDaysChange: (days: number) => void;
    /** tab, y opcionalmente sección dentro de Aterrizaje (overview|actions|patterns|onboarding) */
    onNavigateTab?: (tab: string, section?: string) => void;
}

type ProductSection = 'pulse' | 'depth';

export function ProductPulsePanel({ days, onDaysChange, onNavigateTab }: ProductPulsePanelProps) {
    const { data, isLoading, isError, error, refetch, isFetching } = useProductPulse(days);
    const [section, setSection] = useState<ProductSection>('pulse');

    if (isLoading && !data) {
        return (
            <div className="py-16 flex flex-col items-center justify-center gap-3">
                <RefreshCw size={24} className="animate-spin text-muted-foreground" />
                <p className="text-xs text-muted-foreground">Cargando pulso de producto…</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-6 text-center space-y-3">
                <p className="text-sm font-semibold text-rose-400">No se pudo cargar la pestaña Producto</p>
                <p className="text-xs text-muted-foreground max-w-md mx-auto">
                    {(error as Error)?.message || 'Error al consultar get_product_pulse_analytics'}
                </p>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs font-medium bg-primary text-primary-foreground"
                >
                    <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
                    Reintentar
                </button>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="py-16 text-center text-muted-foreground text-sm">
                No hay datos de producto para este periodo
            </div>
        );
    }

    const chain = data.activation_chain;
    const chainSteps = [
        { key: 'registered', label: 'Registro', value: chain.registered, color: COLORS.ocean },
        { key: 'onboarding_v2_completed', label: 'Onboarding', value: chain.onboarding_v2_completed, color: COLORS.teal },
        { key: 'profile_completed', label: 'Perfil OK', value: chain.profile_completed, color: COLORS.amber },
        { key: 'first_capture', label: '1ª captura', value: chain.first_capture, color: COLORS.emerald },
        { key: 'third_capture', label: '3ª captura', value: chain.third_capture, color: COLORS.purple },
    ];

    function kpiTitle(key: string, label: string): string {
        if (key === 'onboarding_v2_completed') return 'Onboarding OK';
        return label;
    }

    const kpisByCategory = data.kpis.reduce<Record<string, ProductKpi[]>>((acc, kpi) => {
        const cat = kpi.category || 'other';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(kpi);
        return acc;
    }, {});

    const growing = data.feature_momentum.filter(f => f.change_pct > 0).slice(0, 3);
    const declining = [...data.feature_momentum].filter(f => f.change_pct < 0).sort((a, b) => a.change_pct - b.change_pct).slice(0, 3);

    return (
        <div className="space-y-6 min-w-0 max-w-full">
            {/* Period + context */}
            <div className="flex flex-wrap items-center justify-between gap-3 min-w-0">
                <div>
                    <p className="text-sm text-muted-foreground">
                        Comparación con el periodo anterior de igual duración ({data.comparison_label})
                    </p>
                </div>
                <div className="flex gap-1.5 flex-wrap min-w-0">
                    {ANALYTICS_DAY_OPTIONS.map(d => (
                        <button
                            key={d}
                            type="button"
                            onClick={() => onDaysChange(d)}
                            className={cn(
                                'px-3 sm:px-4 py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all shrink-0',
                                days === d
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {d} días
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex flex-wrap gap-1 p-1 bg-muted/50 rounded-lg w-fit max-w-full">
                <button
                    type="button"
                    onClick={() => setSection('pulse')}
                    className={cn(
                        'px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                        section === 'pulse'
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground',
                    )}
                >
                    Pulso
                </button>
                <button
                    type="button"
                    onClick={() => setSection('depth')}
                    className={cn(
                        'px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                        section === 'depth'
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground',
                    )}
                >
                    Profundidad
                </button>
            </div>

            {section === 'depth' && (
                <div className="space-y-6">
                    <ProductGrowthMetricsPanel days={days} />
                    <ProductViralCyclePanel days={days} />
                    <ProductSessionAnalyticsPanel days={days} />
                    <ProductEngagementPanel days={days} />
                    <ProductArticleBehaviorPanel days={days} />
                    <ProductLeanFunnelPanel />
                </div>
            )}

            {section === 'pulse' && data.alerts.length > 0 && (
                <div className="space-y-2">
                    <h3 className="text-sm font-semibold text-foreground">Requiere atención</h3>
                    {data.alerts.map((alert, i) => {
                        const title = alert.title.replace(/Onboarding V2/gi, 'Onboarding');
                        const body = alert.body.replace(/Onboarding V2/gi, 'Onboarding');
                        return (
                        <div
                            key={i}
                            className={cn(
                                'flex gap-3 rounded-xl border p-4',
                                alert.severity === 'warning'
                                    ? 'border-amber-500/30 bg-amber-500/5'
                                    : 'border-primary/30 bg-primary/5'
                            )}
                        >
                            {alert.severity === 'warning'
                                ? <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                                : <Info className="h-5 w-5 text-primary shrink-0 mt-0.5" />}
                            <div>
                                <p className="text-sm font-semibold text-foreground">{title}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{body}</p>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}

            {section === 'pulse' && (
            <>
            {/* Tendencia 7d — KPIs north-star */}
            <div>
                <h3 className="text-sm font-semibold text-foreground mb-3">Tendencia últimos 7 días</h3>
                <ProductPulseTrendCharts trends={data.trends_7d} />
            </div>

            {/* Objetivos trimestrales */}
            <ProductQuarterGoalsPanel
                quarter={data.quarter}
                quarterToDate={data.quarter_to_date}
            />

            {/* Activation chain — lo que un PM mira primero */}
            <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="text-sm font-semibold text-foreground mb-1">Cadena de activación</h3>
                <p className="text-xs text-muted-foreground mb-4">
                    Cohorte de registros del periodo. Cada paso cuenta solo a esos usuarios y el porcentaje es sobre el registro, no sobre el paso anterior.
                </p>
                <div className="space-y-3">
                    {chainSteps.map((step, i) => {
                        const cohort = chainSteps[0].value;
                        const rate = i === 0 ? null : cohortStepPct(step.value, cohort);
                        const widthPct = cohort > 0 && step.value <= cohort
                            ? Math.max(step.value === 0 ? 0 : 4, (step.value / cohort) * 100)
                            : 0;
                        return (
                            <div key={step.key} className="space-y-1">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="font-medium text-foreground">{step.label}</span>
                                    <span className="text-muted-foreground">
                                        {step.value.toLocaleString()}
                                        {i > 0 && (
                                            <span className={cn(
                                                'ml-2 font-semibold',
                                                rate == null
                                                    ? 'text-muted-foreground'
                                                    : rate >= 50 ? 'text-emerald-400' : rate >= 25 ? 'text-amber-400' : 'text-rose-400'
                                            )}>
                                                {rate == null ? '—' : `${rate}% de los registros`}
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <div className="h-2.5 rounded-full bg-muted overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all"
                                        style={{ width: `${widthPct}%`, backgroundColor: step.color }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="mt-4 flex flex-wrap gap-4">
                    {onNavigateTab && (
                        <button
                            type="button"
                            onClick={() => onNavigateTab('landing')}
                            className="text-xs text-primary hover:underline"
                        >
                            Aterrizaje: retención primeras sesiones y patrones →
                        </button>
                    )}
                    {onNavigateTab && (
                        <button
                            type="button"
                            onClick={() => onNavigateTab('landing', 'onboarding')}
                            className="text-xs text-primary hover:underline"
                        >
                            Embudo de onboarding en detalle →
                        </button>
                    )}
                </div>
            </div>

            {/* KPIs by category */}
            {Object.entries(kpisByCategory).map(([category, kpis]) => (
                <div key={category}>
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                        {KPI_CATEGORY_LABELS[category] || category}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                        {kpis.map(kpi => (
                            <MetricCard
                                key={kpi.key}
                                title={kpiTitle(kpi.key, kpi.label)}
                                value={kpi.current}
                                change={kpi.change_pct}
                                changePeriod={data.comparison_label}
                                icon={KPI_ICONS[kpi.key] || <Activity size={18} />}
                            />
                        ))}
                    </div>
                </div>
            ))}

            {/* Onboarding quick insights — resumen; detalle en Aterrizaje */}
            {data.onboarding_insights && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <InsightTile
                        title="Conversión onboarding"
                        value={data.onboarding_insights.v2_completion_rate != null
                            && data.onboarding_insights.v2_completion_rate <= 100
                            ? `${data.onboarding_insights.v2_completion_rate}%`
                            : '—'}
                        subtitle="Registros del periodo que lo completan"
                    />
                    <InsightTile
                        title="Mayor drop-off onboarding"
                        value={data.onboarding_insights.worst_v2_step
                            && data.onboarding_insights.worst_v2_step.drop_pct >= 0
                            && data.onboarding_insights.worst_v2_step.drop_pct <= 100
                            ? data.onboarding_insights.worst_v2_step.label
                            : '—'}
                        subtitle={data.onboarding_insights.worst_v2_step
                            && data.onboarding_insights.worst_v2_step.drop_pct >= 0
                            && data.onboarding_insights.worst_v2_step.drop_pct <= 100
                            ? `${data.onboarding_insights.worst_v2_step.drop_pct}% abandono`
                            : 'Sin datos'}
                    />
                    <InsightTile
                        title="Ubicación concedida"
                        value={data.onboarding_insights.location_grant_rate != null
                            ? `${data.onboarding_insights.location_grant_rate}%`
                            : '—'}
                        subtitle="Tras intro de permiso"
                    />
                </div>
            )}

            {/* Feature momentum */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <MomentumList title="En crecimiento" items={growing} positive />
                <MomentumList title="En descenso" items={declining} positive={false} />
            </div>
            </>
            )}
        </div>
    );
}

function InsightTile({ title, value, subtitle }: { title: string; value: string; subtitle: string }) {
    return (
        <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-xs text-muted-foreground">{title}</p>
            <p className="text-xl font-bold text-foreground mt-1">{value}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>
        </div>
    );
}

function MomentumList({
    title,
    items,
    positive,
}: {
    title: string;
    items: Array<{ label: string; change_pct: number; current: number }>;
    positive: boolean;
}) {
    return (
        <div className="rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">{title}</h3>
            {items.length === 0 ? (
                <p className="text-xs text-muted-foreground">Sin variación significativa</p>
            ) : (
                <ul className="space-y-2">
                    {items.map(item => (
                        <li key={item.label} className="flex items-center justify-between text-sm">
                            <span className="text-foreground">{item.label}</span>
                            <span className={cn(
                                'flex items-center gap-1 font-semibold text-xs',
                                positive ? 'text-emerald-400' : 'text-rose-400'
                            )}>
                                {positive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                                {item.change_pct > 0 ? '+' : ''}{item.change_pct}%
                                <span className="text-muted-foreground font-normal ml-1">
                                    ({item.current.toLocaleString()})
                                </span>
                            </span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
